import {UserRemote} from "@app/core/user/domain/user.remote";
import {EuropastryBaseHandler} from "@app/core/shared/infrastructure/base.handler/europastry-base.handler";
import {LoginHandler} from "@app/core/user/infrastructure/remote/login.handler";
import {Observable, of} from "rxjs";
import {User} from "@app/core/user/domain/entities/user";
import {catchError, map} from "rxjs/operators";
import {UserMapper} from "@app/core/user/infrastructure/mappers/user.mapper";
import {BaseHandleErrorMapper} from "@app/core/shared/infrastructure/base.handler/base-handle-error.mapper";
import {NewCostumerSignUpData} from "@app/core/user/domain/dtos/new-costumer-sign-up-data";
import {ActualCostumerSignUpData} from "@app/core/user/domain/dtos/actual-costumer-sign-up-data";
import {HandlerBuilder, HttpMethod} from "@app/core/shared/infrastructure/base.handler/base-handle.parameters";
import {ApiUrls} from "@app/core/shared/infrastructure/api-urls";
import {Injectable} from "@angular/core";
import {Credentials} from "@app/core/credentials/domain/credentials";
import {UpdateUserData} from "@app/core/user/domain/dtos/update-user-data";
import {CredentialsStorage} from "@app/core/credentials/domain/credentials-storage";
import {EmployeeSignUpData} from "../domain/dtos/employee-sign-up-data";
import {EmployeeSignUpDataMapper} from "./mappers/employee-sign-up-data.mapper";
import {NoDeliveryPostalCodeError} from "@app/core/user/domain/error/no-delivery-postal-code.error";


@Injectable({
    providedIn: "root",
})
export class UserWebService extends UserRemote {

    private NO_DELIVERY_POSTAL_CODE_ERROR_CODE = '400_0018';

    constructor(
        private baseHandler: EuropastryBaseHandler,
        private loginHandler: LoginHandler,
        private credentialsStorage: CredentialsStorage,
        private apiUrls: ApiUrls,
    ) {
        super();
    }

    login(credentials: Credentials, recaptchaToken?: string): Observable<User> {

        return this.loginHandler.execute(credentials, recaptchaToken).pipe(
            map(UserMapper.deserialize),
            catchError(BaseHandleErrorMapper.handleError));
    }

    getUser(): Observable<User | null> {
        const credentials = this.credentialsStorage.getCredentials();
        if (credentials  == null) {
            return of(null);
        }
        const parameters = new HandlerBuilder()
            .method(HttpMethod.get)
            .url(this.apiUrls.GET_USER)
            .authorization(credentials)
            .build()

        return this.baseHandler.execute(parameters).pipe(
            map(UserMapper.deserialize),
            catchError(BaseHandleErrorMapper.handleError)
        );
    }

    signUpNewCustomer(newCostumerSignUpData: NewCostumerSignUpData): Observable<User> {

        const requestBody = {
            name: newCostumerSignUpData.name,
            surname: newCostumerSignUpData.surname,
            email: newCostumerSignUpData.email,
            password: newCostumerSignUpData.password,
            postal_code: newCostumerSignUpData.postalCode,
            user_type: newCostumerSignUpData.userType,
            noDelivery: newCostumerSignUpData.noDelivery,
            recaptchaToken: newCostumerSignUpData.recaptchaToken,
        };

        return this.createNewUser(requestBody)
    }

    signUpActualCustomer(actualCostumerSignUpData: ActualCostumerSignUpData): Observable<User> {

        const requestBody = {
            name: actualCostumerSignUpData.name,
            surname: actualCostumerSignUpData.surname,
            email: actualCostumerSignUpData.email,
            password: actualCostumerSignUpData.password,
            billing_code: actualCostumerSignUpData.nif,
            default_client: actualCostumerSignUpData.customerCode,
            user_type: actualCostumerSignUpData.userType,
            noDelivery: actualCostumerSignUpData.noDelivery,
            recaptchaToken: actualCostumerSignUpData.recaptchaToken,
        };

        return this.createNewUser(requestBody)
    }

    resetPassword(email: string, recaptchaToken: string): Observable<void> {

        const parameters = new HandlerBuilder()
            .method(HttpMethod.post)
            .url(this.apiUrls.RESET_PASSWORD)
            .body({
                email: email,
                recaptchaToken: recaptchaToken,
            })
            .build()

        return this.baseHandler.execute(parameters).pipe(
            catchError(BaseHandleErrorMapper.handleError)
        );
    }

    private createNewUser(data: {}): Observable<User> {
        const parameters = new HandlerBuilder()
            .method(HttpMethod.post)
            .url(this.apiUrls.SIGNUP)
            .body(data)
            .build()

        return this.baseHandler.execute(parameters).pipe(
            map(UserMapper.deserialize),
            catchError(BaseHandleErrorMapper.handleError),
            catchError(error => {
                if (error.apiError?.code === this.NO_DELIVERY_POSTAL_CODE_ERROR_CODE) {
                    throw new NoDeliveryPostalCodeError(error.apiError?.message);
                }
                throw error;
            }),
        );
    }

    updateUser(updateUserData: UpdateUserData): Observable<User> {
        const parameters = new HandlerBuilder()
            .method(HttpMethod.post)
            .url(this.apiUrls.UPDATE_USER)
            .authorization(this.credentialsStorage.getCredentials())
            .body(updateUserData)
            .build()

        return this.baseHandler.execute(parameters).pipe(
            map(UserMapper.deserialize),
            catchError(BaseHandleErrorMapper.handleError)
        );
    }

    signUpEmployee(employeeSignUpData: EmployeeSignUpData): Observable<void> {
        const parameters = new HandlerBuilder()
            .method(HttpMethod.post)
            .url(this.apiUrls.SIGNUP_EMPLOYEE)
            .body(EmployeeSignUpDataMapper.serialize(employeeSignUpData))
            .build()

        return this.baseHandler.execute(parameters).pipe(
            catchError(BaseHandleErrorMapper.handleError)
        );
    }

    public deleteAccount(): Observable<void> {
        const parameters = new HandlerBuilder()
            .method(HttpMethod.delete)
            .url(this.apiUrls.DELETE_USER)
            .authorization(this.credentialsStorage.getCredentials())
            .build()

        return this.baseHandler.execute(parameters).pipe(
            catchError(BaseHandleErrorMapper.handleError)
        );
    }

}
